import React, { useState, useContext } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiX,
  FiEdit2,
  FiTrash2,
  FiClock,
  FiUser,
  FiCalendar,
  FiTag,
  FiCheckCircle,
  FiAlertTriangle,
} from "react-icons/fi";
import { UserContext } from "../../../../context/UserContext";
import { useRoomTasks } from "../../../../context/RoomTasksContext";
import SubtasksList from "./SubtasksList";
import TaskComments from "./TaskComments";
import TaskAttachments from "./TaskAttachments";
import "./TaskDetailModal.css";

const TaskDetailModal = ({ task, roomId, userRole, onClose, onUpdate }) => {
  const { user } = useContext(UserContext);
  const { toggleSubtask, addTaskComment, attachFileToTask, deleteTask } = useRoomTasks();
  const [taskData, setTaskData] = useState(task);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [activeTab, setActiveTab] = useState("details");

  const userId = user?.id || user?._id;
  const isAssignee = task.assignedTo?._id === userId;
  const canEdit = userRole?.isLeader;
  const canDelete = userRole?.isLeader;
  const canToggleSubtasks = isAssignee || userRole?.isLeader;
  const canAttachFiles = isAssignee || userRole?.isLeader;

  const priorityConfig = {
    low: { color: "#10b981", label: "Low", bg: "#ecfdf5" },
    normal: { color: "#6366f1", label: "Normal", bg: "#eef2ff" },
    high: { color: "#f59e0b", label: "High", bg: "#fffbeb" },
    urgent: { color: "#ef4444", label: "Urgent", bg: "#fef2f2" },
  };

  const statusConfig = {
    todo: { color: "#6366f1", label: "To Do" },
    "in-progress": { color: "#f59e0b", label: "In Progress" },
    review: { color: "#8b5cf6", label: "Review" },
    completed: { color: "#10b981", label: "Completed" },
    blocked: { color: "#ef4444", label: "Blocked" },
    overdue: { color: "#dc2626", label: "Overdue" },
  };

  const priority = priorityConfig[taskData.priority] || priorityConfig.normal;
  const status = statusConfig[taskData.status] || statusConfig.todo;

  const formatDate = (dateString) => {
    if (!dateString) return "Not set";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleToggleSubtask = async (subtaskId) => {
    try {
      const result = await toggleSubtask(task._id, subtaskId);
      const updatedTask = {
        ...taskData,
        subtasks: taskData.subtasks.map((sub) =>
          sub._id === subtaskId ? result.subtask : sub
        ),
        progress: result.progress,
      };
      setTaskData(updatedTask);
      onUpdate(updatedTask);
    } catch (error) { /* ignored */ }
  };

  const handleAddComment = async (content) => {
    try {
      const comments = await addTaskComment(task._id, content);
      const updatedTask = { ...taskData, comments };
      setTaskData(updatedTask);
      onUpdate(updatedTask);
    } catch (error) {
throw error;
    }
  };

  const handleAttachFile = async (fileId) => {
    try {
      const attachments = await attachFileToTask(task._id, fileId);
      const updatedTask = { ...taskData, attachments };
      setTaskData(updatedTask);
      onUpdate(updatedTask);
    } catch (error) {
throw error;
    }
  };

  const handleDelete = async () => {
    try {
      await deleteTask(task._id);
      onClose();
    } catch (error) { /* ignored */ }
  };

  const tabs = [
    { id: "details", label: "Details" },
    { id: "subtasks", label: `Subtasks (${taskData.subtasks?.length || 0})` },
    { id: "comments", label: `Comments (${taskData.comments?.length || 0})` },
    { id: "attachments", label: `Files (${taskData.attachments?.length || 0})` },
  ];

  return (
    <div className="rtd-overlay" onClick={onClose}>
      <motion.div
        className="rtd-panel"
        initial={{ opacity: 0, x: 100 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: 100 }}
        transition={{ type: "spring", damping: 30, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="rtd-header">
          <div className="rtd-header-top">
            <div className="rtd-badges">
              <span
                className="rtd-badge rtd-badge--priority"
                style={{ color: priority.color, background: priority.bg }}
              >
                {priority.label}
              </span>
              <span
                className="rtd-badge rtd-badge--status"
                style={{ background: status.color }}
              >
                {status.label}
              </span>
              {taskData.isOverdue && (
                <span className="rtd-badge rtd-badge--overdue">
                  <FiAlertTriangle size={12} /> Overdue
                </span>
              )}
            </div>
            <button className="rtd-close" onClick={onClose}>
              <FiX size={20} />
            </button>
          </div>
          <h2 className="rtd-title">{taskData.title}</h2>
        </div>

        {/* Progress Bar */}
        {taskData.progress > 0 && (
          <div className="rtd-progress">
            <div className="rtd-progress-header">
              <span>Progress</span>
              <span>{taskData.progress}%</span>
            </div>
            <div className="rtd-progress-bar">
              <motion.div
                className="rtd-progress-fill"
                initial={{ width: 0 }}
                animate={{ width: `${taskData.progress}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="rtd-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`rtd-tab ${activeTab === tab.id ? "rtd-tab--active" : ""}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="rtd-content">
          <AnimatePresence mode="wait">
            {activeTab === "details" && (
              <motion.div
                key="details"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="rtd-details"
              >
                {/* Description */}
                <div className="rtd-section">
                  <h3 className="rtd-section-title">Description</h3>
                  <p className="rtd-description">{taskData.description}</p>
                </div>

                {/* Info Grid */}
                <div className="rtd-info-grid">
                  <div className="rtd-info-item">
                    <div className="rtd-info-icon">
                      <FiUser size={16} />
                    </div>
                    <div className="rtd-info-content">
                      <span className="rtd-info-label">Assigned to</span>
                      <div className="rtd-info-value rtd-assignee">
                        {taskData.assignedTo?.profilePhotoUrl ? (
                          <img
                            src={taskData.assignedTo.profilePhotoUrl}
                            alt={taskData.assignedTo.name}
                            className="rtd-assignee-avatar"
                          />
                        ) : (
                          <div className="rtd-assignee-avatar rtd-assignee-avatar--placeholder">
                            {taskData.assignedTo?.name?.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <span>{taskData.assignedTo?.name || "Unassigned"}</span>
                      </div>
                    </div>
                  </div>

                  <div className="rtd-info-item">
                    <div className="rtd-info-icon">
                      <FiCalendar size={16} />
                    </div>
                    <div className="rtd-info-content">
                      <span className="rtd-info-label">Due Date</span>
                      <span className={`rtd-info-value ${taskData.isOverdue ? "rtd-overdue" : ""}`}>
                        {formatDate(taskData.dueDate)}
                      </span>
                    </div>
                  </div>

                  {taskData.category && (
                    <div className="rtd-info-item">
                      <div className="rtd-info-icon">
                        <FiTag size={16} />
                      </div>
                      <div className="rtd-info-content">
                        <span className="rtd-info-label">Category</span>
                        <span className="rtd-info-value">{taskData.category}</span>
                      </div>
                    </div>
                  )}

                  {taskData.estimatedHours && (
                    <div className="rtd-info-item">
                      <div className="rtd-info-icon">
                        <FiClock size={16} />
                      </div>
                      <div className="rtd-info-content">
                        <span className="rtd-info-label">Estimated</span>
                        <span className="rtd-info-value">{taskData.estimatedHours}h</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Labels */}
                {taskData.labels?.length > 0 && (
                  <div className="rtd-section">
                    <h3 className="rtd-section-title">Labels</h3>
                    <div className="rtd-labels">
                      {taskData.labels.map((label, idx) => (
                        <span key={idx} className="rtd-label">{label}</span>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            )}

            {activeTab === "subtasks" && (
              <motion.div
                key="subtasks"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
              >
                <SubtasksList
                  task={taskData}
                  onToggle={handleToggleSubtask}
                  canToggle={canToggleSubtasks}
                />
              </motion.div>
            )}

            {activeTab === "comments" && (
              <motion.div
                key="comments"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
              >
                <TaskComments
                  task={taskData}
                  onAddComment={handleAddComment}
                  currentUser={user}
                />
              </motion.div>
            )}

            {activeTab === "attachments" && (
              <motion.div
                key="attachments"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
              >
                <TaskAttachments
                  task={taskData}
                  roomId={roomId}
                  onAttachFile={handleAttachFile}
                  canAttach={canAttachFiles}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Footer Actions */}
        {(canEdit || canDelete) && (
          <div className="rtd-footer">
            {showDeleteConfirm ? (
              <div className="rtd-delete-confirm">
                <span>Delete this task?</span>
                <div className="rtd-delete-actions">
                  <button className="rtd-btn rtd-btn--danger" onClick={handleDelete}>
                    Yes, Delete
                  </button>
                  <button
                    className="rtd-btn rtd-btn--secondary"
                    onClick={() => setShowDeleteConfirm(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="rtd-actions">
                {canEdit && (
                  <button className="rtd-btn rtd-btn--secondary">
                    <FiEdit2 size={16} /> Edit
                  </button>
                )}
                {canDelete && (
                  <button
                    className="rtd-btn rtd-btn--outline-danger"
                    onClick={() => setShowDeleteConfirm(true)}
                  >
                    <FiTrash2 size={16} /> Delete
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default TaskDetailModal;
